const API_ROOT = '/api';

async function request(path, options = {}) {
  const response = await fetch(`${API_ROOT}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(options.token ? { Authorization: `Bearer ${options.token}` } : {}),
      ...options.headers,
    },
    ...options,
  });

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : null;

  if (!response.ok) {
    const err = new Error(payload?.error || 'Request failed');
    err.status = response.status;
    err.payload = payload;
    throw err;
  }

  return payload;
}

export const api = {
  getTenantInfo() {
    return request('/tenant/me');
  },
  getTenantSettings(token) {
    return request('/tenant-settings', { token });
  },
  updateTenantSettings(payload, token) {
    return request('/tenant-settings', {
      method: 'PATCH',
      token,
      body: JSON.stringify(payload),
    });
  },
  listAdminTenants(token) {
    return request('/_admin/tenants', { token });
  },
  createAdminTenant(payload, token) {
    return request('/_admin/tenants', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    });
  },
  getAdminTenant(id, token) {
    return request(`/_admin/tenants/${id}`, { token });
  },
  updateAdminTenant(id, payload, token) {
    return request(`/_admin/tenants/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(payload),
    });
  },
  listAdminTenantUsers(id, token) {
    return request(`/_admin/tenants/${id}/users`, { token });
  },
  suspendAdminTenantUser(tenantId, userId, reason, token) {
    return request(`/_admin/tenants/${tenantId}/users/${userId}/suspend`, {
      method: 'POST',
      token,
      body: JSON.stringify({ reason }),
    });
  },
  restoreAdminTenantUser(tenantId, userId, token) {
    return request(`/_admin/tenants/${tenantId}/users/${userId}/restore`, {
      method: 'POST',
      token,
    });
  },
  setAdminTenantUserRole(tenantId, userId, role, token) {
    return request(`/_admin/tenants/${tenantId}/users/${userId}/role`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ role }),
    });
  },
  seedAdminTenantSampleCase(tenantId, token) {
    return request(`/_admin/tenants/${tenantId}/seed-sample-case`, {
      method: 'POST',
      token,
    });
  },
  seedAdminTenantCurriculum(tenantId, token, { overwrite = false } = {}) {
    return request(`/_admin/tenants/${tenantId}/seed-curriculum`, {
      method: 'POST',
      token,
      body: JSON.stringify({ overwrite: Boolean(overwrite) }),
    });
  },
  getAdminStatsCases(token, range = {}) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/_admin/stats/cases${suffix}`, { token });
  },
  getAdminStatsStudents(token, range = {}, tenantId) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    if (tenantId) qs.set('tenantId', tenantId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/_admin/stats/students${suffix}`, { token });
  },
  getAdminStatsAuthors(token, range = {}) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/_admin/stats/authors${suffix}`, { token });
  },
  getAdminStatsCaseSessions(caseId, token, range = {}) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/_admin/stats/cases/${caseId}/sessions${suffix}`, { token });
  },
  // ----- Landing-page analytics (platform_admin only) -----
  getAdminLandingAnalytics(token, { days } = {}) {
    const suffix = days ? `?days=${encodeURIComponent(days)}` : '';
    return request(`/_admin/landing-analytics${suffix}`, { token });
  },
  // ----- Demo-request lead triage (platform_admin only) -----
  listAdminDemoRequests(token, { status } = {}) {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/_admin/demo-requests${suffix}`, { token });
  },
  updateAdminDemoRequest(id, payload, token) {
    return request(`/_admin/demo-requests/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(payload),
    });
  },
  // ----- Tenant-scoped stats (educator_admin only) -----
  // Same shape as the admin stats endpoints but filtered to the caller's
  // tenant. Use these from the educator-admin dashboard, not the platform
  // admin one.
  getEducatorStatsCases(token, range = {}) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/stats/cases${suffix}`, { token });
  },
  getEducatorStatsStudents(token, range = {}) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/stats/students${suffix}`, { token });
  },
  getEducatorStatsAuthors(token, range = {}) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/stats/authors${suffix}`, { token });
  },
  getEducatorStatsCaseSessions(caseId, token, range = {}) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/stats/cases/${caseId}/sessions${suffix}`, { token });
  },
  // Per-module rollups + per-drill drill-down (educator_admin, tenant-scoped)
  getEducatorStatsModule(moduleKey, token, range = {}) {
    // moduleKey one of: drills | spot-error-drills | outpatient-prescriptions
    //                 | mcq-exercises | revision-exercises
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/stats/${moduleKey}${suffix}`, { token });
  },
  getEducatorStatsModuleDrillSessions(moduleKey, drillId, token, range = {}) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/stats/${moduleKey}/${drillId}/sessions${suffix}`, { token });
  },
  getEducatorStatsStudentSessions(studentId, token, range = {}) {
    const qs = new URLSearchParams();
    if (range.from) qs.set('from', range.from);
    if (range.to) qs.set('to', range.to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request(`/stats/students/${studentId}/sessions${suffix}`, { token });
  },
  startBillingCheckout(token) {
    return request('/billing/checkout', { method: 'POST', token });
  },
  openBillingPortal(token) {
    return request('/billing/portal', { method: 'POST', token });
  },
  setCaseStudyFreeTier(caseStudyId, freeTierEligible, token) {
    return request(`/admin/case-studies/${caseStudyId}/free-tier`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({ freeTierEligible }),
    });
  },
  login(email, password) {
    return request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
  },
  register(email, password, displayName, role, discipline, inviteCode, referralCode) {
    return request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email, password, displayName, role, discipline,
        ...(inviteCode ? { inviteCode } : {}),
        ...(referralCode ? { referralCode } : {}),
      }),
    });
  },
  getJoinLinkInfo(code) {
    return request(`/join/${encodeURIComponent(code)}`);
  },
  createCohortInviteLink(cohortId, data, token) {
    return request(`/cohorts/${cohortId}/invite-links`, {
      method: 'POST',
      token,
      body: JSON.stringify(data || {}),
    });
  },
  listCohortInviteLinks(cohortId, token) {
    return request(`/cohorts/${cohortId}/invite-links`, { token });
  },
  updateCohortInviteLink(linkId, data, token) {
    return request(`/cohort-invite-links/${linkId}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    });
  },
  deleteCohortInviteLink(linkId, token) {
    return request(`/cohort-invite-links/${linkId}`, { method: 'DELETE', token });
  },
  verifyWitness(username, password, token) {
    return request('/auth/verify-witness', {
      method: 'POST',
      token,
      body: JSON.stringify({ username, password }),
    });
  },
  me(token) {
    return request('/auth/me', { token });
  },
  logout(token) {
    return request('/auth/logout', { method: 'POST', token });
  },
  updateMyDiscipline(discipline, token) {
    return request('/auth/me/discipline', {
      method: 'PATCH',
      token,
      body: JSON.stringify({ discipline }),
    });
  },
  getDrugLibrary() {
    return request('/drug-library');
  },
  getCommonConditions(token) {
    return request('/common-conditions', { token });
  },
  searchTestPatients(searchFields, token) {
    const search = new URLSearchParams();
    Object.entries(searchFields || {}).forEach(([key, value]) => {
      if (value) {
        search.set(key, value);
      }
    });
    return request(`/test-patients/search?${search.toString()}`, { token });
  },
  listRecentTestPatients(token) {
    return request('/test-patients/recent', { token });
  },
  listRecentPatientAccesses(token) {
    return request('/test-patients/recent-accesses', { token });
  },
  getTestPatient(id, token) {
    return request(`/test-patients/${id}`, { token });
  },
  createTestPatient(data, token) {
    return request('/test-patients', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    });
  },
  updateTestPatientAllergies(id, payload, token) {
    return request(`/test-patients/${id}/allergies`, {
      method: 'PUT',
      token,
      body: JSON.stringify(payload),
    });
  },
  updateTestPatientCaseNotes(id, payload, token) {
    return request(`/test-patients/${id}/case-notes`, {
      method: 'PUT',
      token,
      body: JSON.stringify(payload),
    });
  },
  updateTestPatientPrescriptions(id, prescriptions, token) {
    return request(`/test-patients/${id}/prescriptions`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ prescriptions }),
    });
  },
  updateTestPatientDischargePrescription(id, dischargePrescription, token) {
    return request(`/test-patients/${id}/discharge-prescription`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ dischargePrescription }),
    });
  },
  updateTestPatientMeasurements(id, payload, token) {
    return request(`/test-patients/${id}/measurements`, {
      method: 'PUT',
      token,
      body: JSON.stringify(payload),
    });
  },
  deleteTestPatientMeasurement(patientId, measurementId, token) {
    return request(`/test-patients/${patientId}/measurements/${measurementId}`, {
      method: 'DELETE',
      token,
    });
  },
  dischargeTestPatient(id, token) {
    return request(`/test-patients/${id}/discharge`, {
      method: 'POST',
      token,
    });
  },
  readmitTestPatient(id, payload, token) {
    return request(`/test-patients/${id}/readmit`, {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    });
  },
  previewDrugLibraryImport(csvContent, token, replaceExisting = true) {
      return request('/drug-library/import/preview', {
        method: 'POST',
        token,
        body: JSON.stringify({ csvContent, replaceExisting }),
      });
  },
  importDrugLibrary(csvContent, token, replaceExisting = true, confirmedMissing = null) {
      return request('/drug-library/import', {
        method: 'POST',
        token,
        body: JSON.stringify({ csvContent, replaceExisting, confirmedMissing }),
      });
  },
  createDrugLibraryItem(payload, token) {
      return request('/drug-library/items', {
        method: 'POST',
        token,
        body: JSON.stringify(payload),
      });
    },
  createRouteOption(label, token) {
    return request('/admin/routes', {
      method: 'POST',
      token,
      body: JSON.stringify({ label }),
    });
  },
  updateRouteOption(id, label, sortOrder, token) {
    return request(`/admin/routes/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ label, sortOrder }),
    });
  },
  importRouteOptions(csvContent, token) {
    return request('/admin/routes/import', {
      method: 'POST',
      token,
      body: JSON.stringify({ csvContent }),
    });
  },
  deleteRouteOption(id, token) {
    return request(`/admin/routes/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  createFrequencyOption(label, defaultAdminTimes, token) {
    return request('/admin/frequencies', {
      method: 'POST',
      token,
      body: JSON.stringify({ label, defaultAdminTimes }),
    });
  },
  updateFrequencyOption(id, label, defaultAdminTimes, sortOrder, token) {
    return request(`/admin/frequencies/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ label, defaultAdminTimes, sortOrder }),
    });
  },
  importFrequencyOptions(csvContent, token) {
    return request('/admin/frequencies/import', {
      method: 'POST',
      token,
      body: JSON.stringify({ csvContent }),
    });
  },
  deleteFrequencyOption(id, token) {
    return request(`/admin/frequencies/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  createIndicationOption(label, token) {
    return request('/admin/indications', {
      method: 'POST',
      token,
      body: JSON.stringify({ label }),
    });
  },
  updateIndicationOption(id, label, token) {
    return request(`/admin/indications/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ label }),
    });
  },
  importIndicationOptions(csvContent, token) {
    return request('/admin/indications/import', {
      method: 'POST',
      token,
      body: JSON.stringify({ csvContent }),
    });
  },
  deleteIndicationOption(id, token) {
    return request(`/admin/indications/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  createUnitOption(label, token) {
    return request('/admin/units', {
      method: 'POST',
      token,
      body: JSON.stringify({ label }),
    });
  },
  updateUnitOption(id, label, token) {
    return request(`/admin/units/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ label }),
    });
  },
  importUnitOptions(csvContent, token) {
    return request('/admin/units/import', {
      method: 'POST',
      token,
      body: JSON.stringify({ csvContent }),
    });
  },
  deleteUnitOption(id, token) {
    return request(`/admin/units/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  createFormOption(label, token) {
    return request('/admin/forms', {
      method: 'POST',
      token,
      body: JSON.stringify({ label }),
    });
  },
  updateFormOption(id, label, token) {
    return request(`/admin/forms/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ label }),
    });
  },
  importFormOptions(csvContent, token) {
    return request('/admin/forms/import', {
      method: 'POST',
      token,
      body: JSON.stringify({ csvContent }),
    });
  },
  createCriticalMedicine(drugId, token) {
    return request('/admin/critical-medicines', {
      method: 'POST',
      token,
      body: JSON.stringify({ drugId }),
    });
  },
  deleteCriticalMedicine(id, token) {
    return request(`/admin/critical-medicines/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  createControlledDrug(drugId, token) {
    return request('/admin/controlled-drugs', {
      method: 'POST',
      token,
      body: JSON.stringify({ drugId }),
    });
  },
  deleteControlledDrug(id, token) {
    return request(`/admin/controlled-drugs/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  createOrderSet(payload, token) {
    return request('/admin/order-sets', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    });
  },
  updateOrderSet(id, payload, token) {
    return request(`/admin/order-sets/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(payload),
    });
  },
  deleteOrderSet(id, token) {
    return request(`/admin/order-sets/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  deleteFormOption(id, token) {
    return request(`/admin/forms/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  listUserAccounts(token) {
    return request('/admin/users', { token });
  },
  suspendUserAccount(id, reason, token) {
    return request(`/admin/users/${id}/suspend`, {
      method: 'POST',
      token,
      body: JSON.stringify({ reason }),
    });
  },
  restoreUserAccount(id, token) {
    return request(`/admin/users/${id}/restore`, {
      method: 'POST',
      token,
    });
  },
  removeUserAccountAccess(id, reason, token) {
    return request(`/admin/users/${id}/remove-access`, {
      method: 'POST',
      token,
      body: JSON.stringify({ reason }),
    });
  },
  updateDrugLibraryItem(id, payload, token) {
      return request(`/drug-library/items/${id}`, {
        method: 'PUT',
        token,
        body: JSON.stringify(payload),
      });
    },
    deleteDrugLibraryItem(id, token) {
      return request(`/drug-library/items/${id}`, {
        method: 'DELETE',
        token,
      });
    },
  listDrugInteractions(token) {
    return request('/drug-interactions', { token });
  },
  createDrugInteraction(payload, token) {
    return request('/drug-interactions', {
      method: 'POST',
      token,
      body: JSON.stringify(payload || {}),
    });
  },
  updateDrugInteraction(id, payload, token) {
    return request(`/drug-interactions/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(payload || {}),
    });
  },
  deleteDrugInteraction(id, token) {
    return request(`/drug-interactions/${id}`, { method: 'DELETE', token });
  },
  checkDrugInteractions(drugIds, token) {
    return request('/drug-interactions/check', {
      method: 'POST',
      token,
      body: JSON.stringify({ drugIds: Array.isArray(drugIds) ? drugIds : [] }),
    });
  },
  listCaseStudies(token) {
    return request('/case-studies', { token });
  },
  listCaseStudyWorkspace(token) {
    return request('/case-studies/workspace', { token });
  },
  listLibrary(token) {
    return request('/library', { token });
  },
  listCaseStudySetsWorkspace(token) {
    return request('/case-study-sets/workspace', { token });
  },
  getCaseStudy(id, token) {
    return request(`/case-studies/${id}`, { token });
  },
  createCaseStudy(data, token) {
    return request('/case-studies', { method: 'POST', token, body: JSON.stringify({ data }) });
  },
  updateCaseStudy(id, data, token) {
    return request(`/case-studies/${id}`, { method: 'PUT', token, body: JSON.stringify({ data }) });
  },
  cloneCaseStudy(id, token) {
    return request(`/case-studies/${id}/clone`, { method: 'POST', token });
  },
  copySharedCaseStudy(id, token) {
    return request(`/case-studies/${id}/copy-shared`, { method: 'POST', token });
  },
  archiveCaseStudy(id, token) {
    return request(`/case-studies/${id}/archive`, { method: 'POST', token });
  },
  unarchiveCaseStudy(id, token) {
    return request(`/case-studies/${id}/unarchive`, { method: 'POST', token });
  },
  deleteCaseStudy(id, token) {
    return request(`/case-studies/${id}`, { method: 'DELETE', token });
  },
  scheduleCaseStudyPublish(id, payload, token) {
    return request(`/case-studies/${id}/schedule`, {
      method: 'PATCH',
      token,
      body: JSON.stringify({
        scheduledPublishAt: payload?.scheduledPublishAt || null,
        scheduledUnpublishAt: payload?.scheduledUnpublishAt || null,
      }),
    });
  },
  shareCaseStudy(id, email, shareType, token) {
    return request(`/case-studies/${id}/share`, {
      method: 'POST',
      token,
      body: JSON.stringify({ email, shareType }),
    });
  },
  revokeCaseStudyShare(caseId, shareId, token) {
    return request(`/case-studies/${caseId}/shares/${shareId}`, {
      method: 'DELETE',
      token,
    });
  },
  listCohorts(token) {
    return request('/cohorts', { token });
  },
  createCohort(payload, token) {
    return request('/cohorts', {
      method: 'POST',
      token,
      body: JSON.stringify({
        name: payload?.name || '',
        description: payload?.description || '',
      }),
    });
  },
  updateCohort(id, payload, token) {
    return request(`/cohorts/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(payload || {}),
    });
  },
  deleteCohort(id, token) {
    return request(`/cohorts/${id}`, { method: 'DELETE', token });
  },
  listCohortMembers(id, token) {
    return request(`/cohorts/${id}/members`, { token });
  },
  addCohortMember(id, payload, token) {
    return request(`/cohorts/${id}/members`, {
      method: 'POST',
      token,
      body: JSON.stringify(payload || {}),
    });
  },
  removeCohortMember(id, userId, token) {
    return request(`/cohorts/${id}/members/${userId}`, {
      method: 'DELETE',
      token,
    });
  },
  getCaseStudyCohortVisibility(id, token) {
    return request(`/case-studies/${id}/cohort-visibility`, { token });
  },
  setCaseStudyCohortVisibility(id, cohortIds, token) {
    return request(`/case-studies/${id}/cohort-visibility`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ cohortIds: Array.isArray(cohortIds) ? cohortIds : [] }),
    });
  },
  createCaseStudySet(data, token) {
    return request('/case-study-sets', {
      method: 'POST',
      token,
      body: JSON.stringify({ data }),
    });
  },
  updateCaseStudySet(id, data, token) {
    return request(`/case-study-sets/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ data }),
    });
  },
  deleteCaseStudySet(id, token) {
    return request(`/case-study-sets/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  shareCaseStudySet(id, email, token) {
    return request(`/case-study-sets/${id}/share`, {
      method: 'POST',
      token,
      body: JSON.stringify({ email }),
    });
  },
  setCaseStudyStudentAccess(id, enabled, token) {
    return request(`/case-studies/${id}/student-access`, {
      method: 'POST',
      token,
      body: JSON.stringify({ enabled }),
    });
  },
  approveCaseStudy(id, token, body = '') {
    return request(`/case-studies/${id}/approve`, {
      method: 'POST',
      token,
      body: JSON.stringify({ body }),
    });
  },
  rejectCaseStudy(id, reason, token) {
    return request(`/case-studies/${id}/reject`, {
      method: 'POST',
      token,
      body: JSON.stringify({ reason }),
    });
  },
  listCaseStudyReviewComments(id, token) {
    return request(`/case-studies/${id}/review-comments`, { token });
  },
  postCaseStudyReviewComment(id, body, token) {
    return request(`/case-studies/${id}/review-comments`, {
      method: 'POST',
      token,
      body: JSON.stringify({ body }),
    });
  },
  deleteCaseStudyReviewComment(id, commentId, token) {
    return request(`/case-studies/${id}/review-comments/${commentId}`, {
      method: 'DELETE',
      token,
    });
  },
  publishCaseStudy(id, data, mode, token) {
    return request(`/case-studies/${id}/publish`, { method: 'POST', token, body: JSON.stringify({ data, mode }) });
  },
  endLiveClassroom(id, token) {
    return request(`/case-studies/${id}/live-classroom/end`, { method: 'POST', token });
  },
  getCaseAnalytics(id, token) {
    return request(`/case-studies/${id}/analytics`, { token });
  },
  getFacilitatorCaseAttempt(caseId, sessionId, token) {
    return request(`/case-studies/${caseId}/attempts/${sessionId}`, { token });
  },
  saveFacilitatorCaseAttemptReview(caseId, sessionId, mark, feedback, token) {
    return request(`/case-studies/${caseId}/attempts/${sessionId}/review`, {
      method: 'PUT',
      token,
      body: JSON.stringify({ mark, feedback }),
    });
  },
  resetFacilitatorCaseAttempt(caseId, sessionId, token) {
    return request(`/case-studies/${caseId}/attempts/${sessionId}/reset`, {
      method: 'POST',
      token,
    });
  },
  listMySessions(token) {
    return request('/my-sessions', { token });
  },
  favouriteCaseStudy(id, token) {
    return request(`/case-studies/${id}/favourite`, { method: 'POST', token });
  },
  unfavouriteCaseStudy(id, token) {
    return request(`/case-studies/${id}/favourite`, { method: 'DELETE', token });
  },
  startCaseSession(id, token) {
    return request(`/case-studies/${id}/start`, { method: 'POST', token });
  },
  getCaseSession(id, token) {
    return request(`/case-sessions/${id}`, { token });
  },
  saveCaseSession(id, answers, progress, token, caseSnapshot = undefined) {
    return request(`/case-sessions/${id}`, { method: 'PUT', token, body: JSON.stringify({ answers, progress, caseSnapshot }) });
  },
  completeCaseSession(id, answers, progress, token, caseSnapshot = undefined) {
    return request(`/case-sessions/${id}/complete`, { method: 'POST', token, body: JSON.stringify({ answers, progress, caseSnapshot }) });
  },
  getSession(sessionCode) {
    return request(`/sessions/${sessionCode}`);
  },
  getSessionResponses(sessionCode) {
    return request(`/sessions/${sessionCode}/responses`);
  },
  joinSession(sessionCode, participantId, participantName, token) {
    return request(`/sessions/${sessionCode}/join`, {
      method: 'POST',
      token,
      body: JSON.stringify({ participantId, participantName }),
    });
  },
  pushSession(sessionCode, data, token) {
    return request(`/sessions/${sessionCode}/push`, { method: 'POST', token, body: JSON.stringify({ data }) });
  },
  submitSessionResponse(sessionCode, questionNumber, answer, participantId, participantName, token) {
    return request(`/sessions/${sessionCode}/responses`, {
      method: 'POST',
      token,
      body: JSON.stringify({ questionNumber, answer, participantId, participantName }),
    });
  },
  controlSession(sessionCode, stepIndex, revealAnswers, token, presentationStage = undefined, stageIndex = undefined, revealedQuestionNumbers = undefined) {
    return request(`/sessions/${sessionCode}/control`, {
      method: 'POST',
      token,
      body: JSON.stringify({ stepIndex, revealAnswers, presentationStage, stageIndex, revealedQuestionNumbers }),
    });
  },
  // ----- Calculation drill module -----
  listDrillsWorkspace(token) {
    return request('/drills/workspace', { token });
  },
  listDrills(token) {
    return request('/drills', { token });
  },
  getDrill(id, token) {
    return request(`/drills/${id}`, { token });
  },
  createDrill(payload, token) {
    return request('/drills', { method: 'POST', token, body: JSON.stringify(payload) });
  },
  updateDrill(id, payload, token) {
    return request(`/drills/${id}`, { method: 'PUT', token, body: JSON.stringify(payload) });
  },
  publishDrill(id, token) {
    return request(`/drills/${id}/publish`, { method: 'POST', token });
  },
  archiveDrill(id, token) {
    return request(`/drills/${id}/archive`, { method: 'POST', token });
  },
  deleteDrill(id, token) {
    return request(`/drills/${id}`, { method: 'DELETE', token });
  },
  startDrill(id, token, options = {}) {
    const body = options.assignmentId ? JSON.stringify({ assignmentId: options.assignmentId }) : undefined;
    return request(`/drills/${id}/start`, { method: 'POST', token, body });
  },
  getDrillSession(sessionId, token) {
    return request(`/drills/sessions/${sessionId}`, { token });
  },
  submitDrillAnswer(sessionId, questionId, answer, token) {
    return request(`/drills/sessions/${sessionId}/answer`, {
      method: 'POST',
      token,
      body: JSON.stringify({ questionId, answer }),
    });
  },
  submitDrillSession(sessionId, token) {
    return request(`/drills/sessions/${sessionId}/submit`, { method: 'POST', token });
  },
  validateDrillFormula(formula, variables, token) {
    return request('/drills/validate-formula', {
      method: 'POST',
      token,
      body: JSON.stringify({ formula, variables }),
    });
  },
  // ----- MCQ exercises module -----
  listMcqExercisesWorkspace(token) {
    return request('/mcq-exercises/workspace', { token });
  },
  listMcqExercises(token) {
    return request('/mcq-exercises', { token });
  },
  getMcqExercise(id, token) {
    return request(`/mcq-exercises/${id}`, { token });
  },
  createMcqExercise(payload, token) {
    return request('/mcq-exercises', { method: 'POST', token, body: JSON.stringify(payload) });
  },
  updateMcqExercise(id, payload, token) {
    return request(`/mcq-exercises/${id}`, { method: 'PUT', token, body: JSON.stringify(payload) });
  },
  publishMcqExercise(id, token) {
    return request(`/mcq-exercises/${id}/publish`, { method: 'POST', token });
  },
  archiveMcqExercise(id, token) {
    return request(`/mcq-exercises/${id}/archive`, { method: 'POST', token });
  },
  deleteMcqExercise(id, token) {
    return request(`/mcq-exercises/${id}`, { method: 'DELETE', token });
  },
  copyMcqsFromDrill(drillId, token) {
    return request(`/mcq-exercises/from-drill/${drillId}`, { method: 'POST', token });
  },
  startMcqExercise(id, token) {
    return request(`/mcq-exercises/${id}/start`, { method: 'POST', token });
  },
  getMcqSession(sessionId, token) {
    return request(`/mcq-sessions/${sessionId}`, { token });
  },
  submitMcqAnswer(sessionId, questionId, answer, token) {
    return request(`/mcq-sessions/${sessionId}/answer`, {
      method: 'POST',
      token,
      body: JSON.stringify({ questionId, answer }),
    });
  },
  submitMcqSession(sessionId, token) {
    return request(`/mcq-sessions/${sessionId}/submit`, { method: 'POST', token });
  },
  // ----- Question bank items (per-type item library) -----
  // filter accepts { type, status, category, difficulty } — pass `type`
  // as a comma-joined string of templateType values (e.g. "ordering,bucket-sort,matching")
  // to fetch the whole drag-and-drop group in one request.
  listQuestionBankItems(token, filter = {}) {
    const params = new URLSearchParams();
    if (filter.type) params.set('type', String(filter.type));
    if (filter.status) params.set('status', String(filter.status));
    if (filter.category) params.set('category', String(filter.category));
    if (filter.difficulty) params.set('difficulty', String(filter.difficulty));
    const qs = params.toString();
    return request(`/question-bank-items${qs ? `?${qs}` : ''}`, { token });
  },
  getQuestionBankItem(id, token) {
    return request(`/question-bank-items/${id}`, { token });
  },
  createQuestionBankItem(payload, token) {
    return request('/question-bank-items', { method: 'POST', token, body: JSON.stringify(payload) });
  },
  updateQuestionBankItem(id, payload, token) {
    return request(`/question-bank-items/${id}`, { method: 'PUT', token, body: JSON.stringify(payload) });
  },
  publishQuestionBankItem(id, token) {
    return request(`/question-bank-items/${id}/publish`, { method: 'POST', token });
  },
  archiveQuestionBankItem(id, token) {
    return request(`/question-bank-items/${id}/archive`, { method: 'POST', token });
  },
  deleteQuestionBankItem(id, token) {
    return request(`/question-bank-items/${id}`, { method: 'DELETE', token });
  },
  // ----- Curated revision exercises -----
  listRevisionExercisesWorkspace(token) {
    return request('/revision-exercises/workspace', { token });
  },
  listRevisionExercises(token) {
    return request('/revision-exercises', { token });
  },
  getRevisionExercise(id, token) {
    return request(`/revision-exercises/${id}`, { token });
  },
  createRevisionExercise(payload, token) {
    return request('/revision-exercises', { method: 'POST', token, body: JSON.stringify(payload) });
  },
  updateRevisionExercise(id, payload, token) {
    return request(`/revision-exercises/${id}`, { method: 'PUT', token, body: JSON.stringify(payload) });
  },
  publishRevisionExercise(id, token) {
    return request(`/revision-exercises/${id}/publish`, { method: 'POST', token });
  },
  archiveRevisionExercise(id, token) {
    return request(`/revision-exercises/${id}/archive`, { method: 'POST', token });
  },
  deleteRevisionExercise(id, token) {
    return request(`/revision-exercises/${id}`, { method: 'DELETE', token });
  },
  listImportableQuestions(token) {
    return request('/revision-exercises/importable', { token });
  },
  startRevisionExercise(id, token) {
    return request(`/revision-exercises/${id}/start`, { method: 'POST', token });
  },
  // ----- Mixed-revision sessions -----
  startRevisionSession(length, token, filters = {}) {
    return request('/revision-sessions/start', {
      method: 'POST',
      token,
      body: JSON.stringify({
        length,
        categories: Array.isArray(filters.categories) ? filters.categories : [],
        difficulties: Array.isArray(filters.difficulties) ? filters.difficulties : [],
        questionTypes: Array.isArray(filters.questionTypes) ? filters.questionTypes : [],
      }),
    });
  },
  getRevisionSession(sessionId, token) {
    return request(`/revision-sessions/${sessionId}`, { token });
  },
  submitRevisionAnswer(sessionId, questionId, answer, token) {
    return request(`/revision-sessions/${sessionId}/answer`, {
      method: 'POST',
      token,
      body: JSON.stringify({ questionId, answer }),
    });
  },
  submitRevisionSession(sessionId, token) {
    return request(`/revision-sessions/${sessionId}/submit`, { method: 'POST', token });
  },
  getRevisionBests(token) {
    return request('/revision-sessions/best', { token });
  },
  // ----- Exercise assignments -----
  listExerciseAssignments(token) {
    return request('/exercise-assignments', { token });
  },
  listMyExerciseAssignments(token) {
    return request('/exercise-assignments/mine', { token });
  },
  getExerciseAssignment(id, token) {
    return request(`/exercise-assignments/${id}`, { token });
  },
  getExerciseAssignmentResults(id, token) {
    return request(`/exercise-assignments/${id}/results`, { token });
  },
  createExerciseAssignment(payload, token) {
    return request('/exercise-assignments', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    });
  },
  updateExerciseAssignment(id, payload, token) {
    return request(`/exercise-assignments/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(payload),
    });
  },
  deleteExerciseAssignment(id, token) {
    return request(`/exercise-assignments/${id}`, { method: 'DELETE', token });
  },
  // Live drill (educator + student)
  startLiveDrill(drillId, token) {
    return request(`/drills/${drillId}/live-sessions`, { method: 'POST', token });
  },
  getLiveDrillByCode(code) {
    return request(`/live-drill-sessions/${encodeURIComponent(String(code).toUpperCase())}`);
  },
  submitLiveDrillResponse(sessionId, payload) {
    return request(`/live-drill-sessions/${sessionId}/responses`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  advanceLiveDrill(sessionId, token) {
    return request(`/live-drill-sessions/${sessionId}/advance`, { method: 'POST', token });
  },
  revealLiveDrill(sessionId, token) {
    return request(`/live-drill-sessions/${sessionId}/reveal`, { method: 'POST', token });
  },
  closeLiveDrill(sessionId, token) {
    return request(`/live-drill-sessions/${sessionId}/close`, { method: 'POST', token });
  },
  getLiveDrillAggregate(sessionId, token) {
    return request(`/live-drill-sessions/${sessionId}/aggregate`, { token });
  },
  // Admin tenant seed
  seedAdminTenantDrills(tenantId, token, { overwrite = false } = {}) {
    return request(`/_admin/tenants/${tenantId}/seed-drills`, {
      method: 'POST',
      token,
      body: JSON.stringify({ overwrite: Boolean(overwrite) }),
    });
  },
  // ----- Spot-the-Error drill module -----
  listSpotErrorDrills(token) {
    return request('/spot-error-drills', { token });
  },
  listSpotErrorDrillsAll(token) {
    return request('/spot-error-drills/all', { token });
  },
  publishSpotErrorDrill(id, token) {
    return request(`/spot-error-drills/${id}/publish`, { method: 'POST', token });
  },
  archiveSpotErrorDrill(id, token) {
    return request(`/spot-error-drills/${id}/archive`, { method: 'POST', token });
  },
  getSpotErrorDrill(id, token) {
    return request(`/spot-error-drills/${id}`, { token });
  },
  createSpotErrorDrill(payload, token) {
    return request('/spot-error-drills', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    });
  },
  updateSpotErrorDrill(id, payload, token) {
    return request(`/spot-error-drills/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(payload),
    });
  },
  deleteSpotErrorDrill(id, token) {
    return request(`/spot-error-drills/${id}`, { method: 'DELETE', token });
  },
  startSpotErrorDrill(id, token) {
    return request(`/spot-error-drills/${id}/start`, { method: 'POST', token });
  },
  getSpotErrorSession(sessionId, token) {
    return request(`/spot-error-sessions/${sessionId}`, { token });
  },
  submitSpotErrorSession(sessionId, flags, durationSeconds, token) {
    return request(`/spot-error-sessions/${sessionId}/submit`, {
      method: 'POST',
      token,
      body: JSON.stringify({ flags, durationSeconds }),
    });
  },
  seedAdminTenantSpotErrorDrills(tenantId, token, { overwrite = false } = {}) {
    return request(`/_admin/tenants/${tenantId}/seed-spot-error-drills`, {
      method: 'POST',
      token,
      body: JSON.stringify({ overwrite: Boolean(overwrite) }),
    });
  },
  // ----- Outpatient prescription drills -----
  listOutpatientDrills(token) {
    return request('/outpatient-drills', { token });
  },
  listOutpatientDrillsAll(token) {
    return request('/outpatient-drills/all', { token });
  },
  publishOutpatientDrill(id, token) {
    return request(`/outpatient-drills/${id}/publish`, { method: 'POST', token });
  },
  archiveOutpatientDrill(id, token) {
    return request(`/outpatient-drills/${id}/archive`, { method: 'POST', token });
  },
  getOutpatientDrill(id, token) {
    return request(`/outpatient-drills/${id}`, { token });
  },
  createOutpatientDrill(payload, token) {
    return request('/outpatient-drills', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    });
  },
  updateOutpatientDrill(id, payload, token) {
    return request(`/outpatient-drills/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(payload),
    });
  },
  deleteOutpatientDrill(id, token) {
    return request(`/outpatient-drills/${id}`, { method: 'DELETE', token });
  },
  startOutpatientDrill(id, token) {
    return request(`/outpatient-drills/${id}/start`, { method: 'POST', token });
  },
  submitOutpatientSession(sessionId, submission, token) {
    return request(`/outpatient-sessions/${sessionId}/submit`, {
      method: 'POST',
      token,
      body: JSON.stringify({ submission }),
    });
  },
  previewOutpatientDrillStart(payload, token) {
    return request('/outpatient-drills/preview-start', {
      method: 'POST',
      token,
      body: JSON.stringify({ payload }),
    });
  },
  previewOutpatientDrillGrade(payload, submission, token) {
    return request('/outpatient-drills/preview-grade', {
      method: 'POST',
      token,
      body: JSON.stringify({ payload, submission }),
    });
  },
  seedAdminTenantOutpatient(tenantId, token, { overwrite = false } = {}) {
    return request(`/_admin/tenants/${tenantId}/seed-outpatient-prescriptions`, {
      method: 'POST',
      token,
      body: JSON.stringify({ overwrite: Boolean(overwrite) }),
    });
  },
  // Cross-module review queue (broadened in /api/reviews/*). The earlier
  // /outpatient-review/* endpoints stay on the server but the UI now calls
  // these so it works for any module type.
  listReviewQueue(token, { includeReviewed = false } = {}) {
    const qs = includeReviewed ? '?includeReviewed=true' : '';
    return request(`/reviews/queue${qs}`, { token });
  },
  getReviewPendingCount(token) {
    return request('/reviews/pending-count', { token });
  },
  getReviewSession(moduleType, sessionId, token) {
    return request(`/reviews/sessions/${moduleType}/${sessionId}`, { token });
  },
  markSessionReviewed(moduleType, sessionId, { comment, scoreOverride, scoreOverrideReason } = {}, token) {
    const body = { comment: comment || '' };
    if (scoreOverride !== undefined) {
      body.scoreOverride = scoreOverride;
      if (scoreOverride !== null) body.scoreOverrideReason = scoreOverrideReason || null;
    }
    return request(`/reviews/sessions/${moduleType}/${sessionId}/review`, {
      method: 'POST',
      token,
      body: JSON.stringify(body),
    });
  },
  reopenReviewSession(moduleType, sessionId, token) {
    return request(`/reviews/sessions/${moduleType}/${sessionId}/reopen`, {
      method: 'POST',
      token,
    });
  },
  flagSessionForReview(moduleType, sessionId, token) {
    return request(`/reviews/sessions/${moduleType}/${sessionId}/flag`, {
      method: 'POST',
      token,
    });
  },
  getMySubmissions(token) {
    return request('/student/submissions', { token });
  },
  getPortfolio(token) {
    return request('/portfolio', { token });
  },
  createCpdEntry(payload, token) {
    return request('/portfolio/cpd-entries', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    });
  },
  updateCpdEntry(id, payload, token) {
    return request(`/portfolio/cpd-entries/${id}`, {
      method: 'PUT',
      token,
      body: JSON.stringify(payload),
    });
  },
  deleteCpdEntry(id, token) {
    return request(`/portfolio/cpd-entries/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  upsertPortfolioReflection({ entryKind, entryId, body }, token) {
    return request('/portfolio/reflections', {
      method: 'PUT',
      token,
      body: JSON.stringify({ entryKind, entryId, body }),
    });
  },
  deletePortfolioReflection({ entryKind, entryId }, token) {
    return request('/portfolio/reflections', {
      method: 'DELETE',
      token,
      body: JSON.stringify({ entryKind, entryId }),
    });
  },
  getPortfolioFrameworks(token) {
    return request('/portfolio/frameworks', { token });
  },
  enablePortfolioFramework(frameworkId, token) {
    return request(`/portfolio/frameworks/${frameworkId}/enable`, {
      method: 'POST',
      token,
    });
  },
  disablePortfolioFramework(frameworkId, token) {
    return request(`/portfolio/frameworks/${frameworkId}/disable`, {
      method: 'POST',
      token,
    });
  },
  getPortfolioOutcomes(frameworkIdOrSlug, token) {
    return request(`/portfolio/outcomes?framework=${encodeURIComponent(frameworkIdOrSlug)}`, { token });
  },
  setPortfolioTags({ entryKind, entryId, outcomeItemIds }, token) {
    return request('/portfolio/tags', {
      method: 'POST',
      token,
      body: JSON.stringify({ entryKind, entryId, outcomeItemIds }),
    });
  },
  listPortfolios(token) {
    return request('/portfolios', { token });
  },
  createPortfolio(payload, token) {
    return request('/portfolios', {
      method: 'POST',
      token,
      body: JSON.stringify(payload),
    });
  },
  updatePortfolio(id, payload, token) {
    return request(`/portfolios/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(payload),
    });
  },
  deletePortfolio(id, token) {
    return request(`/portfolios/${id}`, {
      method: 'DELETE',
      token,
    });
  },
  getPortfolioById(id, token) {
    return request(`/portfolios/${id}`, { token });
  },
  addPortfolioEntry(id, { entryKind, entryId }, token) {
    return request(`/portfolios/${id}/entries`, {
      method: 'POST',
      token,
      body: JSON.stringify({ entryKind, entryId }),
    });
  },
  removePortfolioEntry(id, { entryKind, entryId }, token) {
    return request(`/portfolios/${id}/entries`, {
      method: 'DELETE',
      token,
      body: JSON.stringify({ entryKind, entryId }),
    });
  },
  listStudentsWithPortfolios(token) {
    return request('/educator/portfolio/students', { token });
  },
  getStudentPortfolioDetail(userId, token) {
    return request(`/educator/portfolio/students/${userId}`, { token });
  },
  listPortfolioTriage(token) {
    return request('/educator/portfolio/triage', { token });
  },
  listPortfolioGoals(token) {
    return request('/portfolio/goals', { token });
  },
  createPortfolioGoal(payload, token) {
    return request('/portfolio/goals', {
      method: 'POST',
      token,
      body: JSON.stringify(payload || {}),
    });
  },
  updatePortfolioGoal(id, payload, token) {
    return request(`/portfolio/goals/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(payload || {}),
    });
  },
  deletePortfolioGoal(id, token) {
    return request(`/portfolio/goals/${id}`, { method: 'DELETE', token });
  },
  linkPortfolioGoalEntry(id, { entryKind, entryId }, token) {
    return request(`/portfolio/goals/${id}/links`, {
      method: 'POST',
      token,
      body: JSON.stringify({ entryKind, entryId }),
    });
  },
  unlinkPortfolioGoalEntry(id, { entryKind, entryId }, token) {
    return request(`/portfolio/goals/${id}/links`, {
      method: 'DELETE',
      token,
      body: JSON.stringify({ entryKind, entryId }),
    });
  },
  listNotifications(token) {
    return request('/notifications', { token });
  },
  markNotificationRead(id, token) {
    return request(`/notifications/${id}/read`, { method: 'POST', token });
  },
  markAllNotificationsRead(token) {
    return request('/notifications/read-all', { method: 'POST', token });
  },
  upsertLecturerComment({ studentUserId, entryKind, entryId, body }, token) {
    return request('/educator/portfolio/comments', {
      method: 'POST',
      token,
      body: JSON.stringify({ studentUserId, entryKind, entryId, body }),
    });
  },
  deleteLecturerComment({ entryKind, entryId }, token) {
    return request('/educator/portfolio/comments', {
      method: 'DELETE',
      token,
      body: JSON.stringify({ entryKind, entryId }),
    });
  },
  listPortfolioShareLinks(portfolioId, token) {
    return request(`/portfolios/${portfolioId}/share-links`, { token });
  },
  createPortfolioShareLink(portfolioId, payload, token) {
    return request(`/portfolios/${portfolioId}/share-links`, {
      method: 'POST',
      token,
      body: JSON.stringify(payload || {}),
    });
  },
  updatePortfolioShareLink(portfolioId, linkId, payload, token) {
    return request(`/portfolios/${portfolioId}/share-links/${linkId}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(payload || {}),
    });
  },
  deletePortfolioShareLink(portfolioId, linkId, token) {
    return request(`/portfolios/${portfolioId}/share-links/${linkId}`, {
      method: 'DELETE',
      token,
    });
  },
  getPublicPortfolioShare(token) {
    // No auth - access is gated entirely by the unguessable token
    return request(`/public/portfolio-shares/${token}`);
  },
};

export function createSessionEventSource(sessionCode) {
  return new EventSource(`${API_ROOT}/sessions/${sessionCode}/stream`);
}
